import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import React, {useEffect, useMemo, useState} from 'react';
import {styles} from './styles';
import {useFilterStore} from '../../../../store/FilterStore';
import {IBrand, ISegment} from '../../../../types/Filter';

interface IFilter {
  brands?: string[];
  category?: string;
  search?: string;
  sort?: string;
  news?: boolean;
  combo?: boolean;
  packaging?: string;
}

interface IModalFilter {
  visible: boolean;
  onRequestClose: () => void;
  returnFilter: (filter: IFilter) => void;
  cleanFilter: () => void;
  onPressNews?: () => void;
  onPressCombo?: () => void;
  hasOrder?: boolean;
  hasCategory?: boolean;
  hasBrand?: boolean;
  hasNews?: boolean;
  hasSize?: boolean;
  hasNewCampaigns?: boolean;
  HasOngoingCampaigns?: boolean;
  hasCombo?: boolean;
  hasPackaging?: boolean;
  packagingList?: string[];
  currentPackaging?: string;
  currentSort?: string;
  currentCategory?: string;
  currentBrands?: string[];
  /** Modo "Ver apenas novidades" ativo (busca específica, exclusiva com combo). */
  currentNews?: boolean;
  /** Modo "Ver apenas combos" ativo (categoria Combo, exclusiva com novidade). */
  currentCombo?: boolean;
  /** Quando true, categoria está travada — exibe seção Marca em vez de Categoria. */
  lockedCategory?: boolean;
  /** Lista de marcas a exibir quando lockedCategory=true. */
  brandList?: IBrand[];
}

const SORT_OPTIONS = [
  {value: 'price_asc', label: 'Menor Preço'},
  {value: 'name_asc', label: 'Nome de (A - Z)'},
  {value: 'name_desc', label: 'Nome de (Z - A)'},
  {value: 'price_desc', label: 'Maior Preço'},
];

const ModalFilter = ({
  visible,
  onRequestClose,
  cleanFilter,
  hasCombo,
  hasCategory,
  hasBrand,
  hasNews,
  hasOrder,
  hasPackaging,
  packagingList,
  currentPackaging,
  returnFilter,
  currentSort,
  currentCategory,
  currentBrands,
  currentNews = false,
  currentCombo = false,
  lockedCategory = false,
  brandList,
}: IModalFilter) => {
  const {category} = useFilterStore();
  const [filter, setFilter] = useState<IFilter>({
    category: currentCategory || '',
    brands: currentBrands || [],
    search: '',
    sort: currentSort || '',
    packaging: currentPackaging || '',
    news: currentNews,
    combo: currentCombo,
  });

  useEffect(() => {
    setFilter(prev => ({
      ...prev,
      category: currentCategory || '',
      brands: currentBrands || [],
      sort: currentSort || '',
      packaging: currentPackaging || '',
      news: currentNews,
      combo: currentCombo,
    }));
  }, [currentCategory, currentBrands, currentSort, currentPackaging, currentNews, currentCombo]);

  const sortedPackagingList = useMemo(() => {
    if (!packagingList) return [];
    const otherItems = packagingList.filter(item => item !== '');
    const hasEmpty = packagingList.includes('');

    if (hasEmpty) {
      return ['', ...otherItems];
    }
    return otherItems;
  }, [packagingList]);

  const handleCleanFilter = () => {
    setFilter({
      category: undefined,
      brands: [],
      search: undefined,
      sort: undefined,
      packaging: undefined,
      news: false,
      combo: false,
    });
    cleanFilter();
    onRequestClose();
  };

  const handleApplyFilter = () => {
    returnFilter(filter);
    onRequestClose();
  };

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={visible}
      onRequestClose={onRequestClose}>
      <View style={styles.overlay}>
        <View style={styles.container}>
          <View style={styles.header}>
            <Text style={styles.title}>Filtrar</Text>
            <TouchableOpacity onPress={onRequestClose}>
              <Text style={styles.closeButton}>✕</Text>
            </TouchableOpacity>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>
            {hasOrder && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Ordenar</Text>
                {SORT_OPTIONS.map(option => (
                  <TouchableOpacity
                    key={option.value}
                    style={[
                      styles.option,
                      filter.sort === option.value && styles.optionSelected,
                    ]}
                    onPress={() =>
                      setFilter(prev => ({...prev, sort: option.value}))
                    }>
                    <Text
                      style={[
                        styles.optionText,
                        filter.sort === option.value &&
                          styles.optionTextSelected,
                      ]}>
                      {option.label}
                    </Text>
                    {filter.sort === option.value && (
                      <View style={styles.radioSelected} />
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {(lockedCategory || hasBrand) && brandList && brandList.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Marca</Text>
                {brandList.map((item: IBrand, index: number) => {
                  const isSelected = (filter.brands ?? []).includes(item.name);
                  return (
                    <TouchableOpacity
                      key={index}
                      style={[styles.option, isSelected && styles.optionSelected]}
                      onPress={() =>
                        setFilter(prev => {
                          const current = prev.brands ?? [];
                          const next = current.includes(item.name)
                            ? current.filter(b => b !== item.name)
                            : [...current, item.name];
                          return {...prev, brands: next};
                        })
                      }>
                      <Text
                        style={[
                          styles.optionText,
                          isSelected && styles.optionTextSelected,
                        ]}>
                        {item.name}
                      </Text>
                      {isSelected && <View style={styles.checkSelected} />}
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}

            {hasCategory && !lockedCategory && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Categoria</Text>
                {category.map((item: ISegment, index: number) => (
                  <TouchableOpacity
                    key={index}
                    style={[
                      styles.option,
                      filter.category === item.name && styles.optionSelected,
                    ]}
                    onPress={() =>
                      setFilter(prev => ({
                        ...prev,
                        category: item.name,
                        news: false,
                        combo: false,
                      }))
                    }>
                    <Text
                      style={[
                        styles.optionText,
                        filter.category === item.name &&
                          styles.optionTextSelected,
                      ]}>
                      {item.name}
                    </Text>
                    {filter.category === item.name && (
                      <View style={styles.radioSelected} />
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {hasPackaging && sortedPackagingList && sortedPackagingList.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Embalagem</Text>
                {sortedPackagingList.map((item: string, index: number) => (
                  <TouchableOpacity
                    key={index}
                    style={[
                      styles.option,
                      filter.packaging === item && styles.optionSelected,
                    ]}
                    onPress={() =>
                      setFilter(prev => ({...prev, packaging: item}))
                    }>
                    <Text
                      style={[
                        styles.optionText,
                        filter.packaging === item && styles.optionTextSelected,
                      ]}>
                      {item === '' ? 'Todas' : item}
                    </Text>
                    {filter.packaging === item && (
                      <View style={styles.radioSelected} />
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {hasNews && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Novidade</Text>
                <TouchableOpacity
                  style={[
                    styles.option,
                    filter.news && styles.optionSelected,
                  ]}
                  onPress={() =>
                    setFilter(prev => ({...prev, news: true, combo: false}))
                  }>
                  <Text
                    style={[
                      styles.optionText,
                      filter.news && styles.optionTextSelected,
                    ]}>
                    Ver apenas novidades
                  </Text>
                  {filter.news && <View style={styles.radioSelected} />}
                </TouchableOpacity>
              </View>
            )}

            {hasCombo && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Combo</Text>
                <TouchableOpacity
                  style={[
                    styles.option,
                    filter.combo && styles.optionSelected,
                  ]}
                  onPress={() =>
                    setFilter(prev => ({...prev, news: false, combo: true}))
                  }>
                  <Text
                    style={[
                      styles.optionText,
                      filter.combo && styles.optionTextSelected,
                    ]}>
                    Ver apenas combos
                  </Text>
                  {filter.combo && <View style={styles.radioSelected} />}
                </TouchableOpacity>
              </View>
            )}
          </ScrollView>

          <View style={styles.footer}>
            <TouchableOpacity
              style={styles.buttonClean}
              onPress={handleCleanFilter}>
              <Text style={styles.buttonCleanText}>Limpar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.buttonApply}
              onPress={handleApplyFilter}>
              <Text style={styles.buttonApplyText}>Aplicar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default ModalFilter;
